import { useState, useEffect } from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { api } from './apiClient';
import Login from './components/Login';
import { AppShell, Group, Modal, Text, ActionIcon, useMantineColorScheme, Box, Menu, Tooltip, UnstyledButton } from '@mantine/core';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Study from './pages/Study';
import Admin from './pages/Admin';
import Certifications from './pages/Certifications';
import ExamViewWrapper from './components/ExamViewWrapper';
import AdminRoute from './components/admin/AdminRoute';
import { IconSchool, IconUser, IconSun, IconMoon, IconLogout, IconSettings, IconAward } from '@tabler/icons-react';
import { isAdmin } from './utils/authUtils';

function App() {
  const navigate = useNavigate();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const [user, setUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [certificationCodes, setCertificationCodes] = useState([]);
  // 管理者かどうかをヘッダーアイコン表示のために保持する
  const [adminUser, setAdminUser] = useState(false);
  useEffect(() => {
    // 初回ロード時にサインイン済みかを確認する
    getCurrentUser()
      .then((cognitoUser) => {
        setUser(cognitoUser);
        setIsModalOpen(false);
        isAdmin(cognitoUser).then(setAdminUser);
      })
      .catch(() => {
        setUser(null);
        setIsModalOpen(true);
      });

    // 認証状態変化（サインイン / サインアウト）を監視する
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        getCurrentUser().then((cognitoUser) => {
          setUser(cognitoUser);
          setIsModalOpen(false);
          isAdmin(cognitoUser).then(setAdminUser);
        });
      } else if (payload.event === 'signedOut') {
        setUser(null);
        setAdminUser(false);
        setIsModalOpen(true);
        navigate('/Study', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // certifications テーブルから資格コード一覧を取得し、ルーティングに利用する
    if (!user) return;
    const fetchCertifications = async () => {
      try {
        const data = await api.get('/certifications', { field: 'code' });
        const uniqueCodes = Array.from(
          new Set((data || []).map((row) => row?.code).filter(Boolean))
        );
        setCertificationCodes(uniqueCodes);
      } catch (e) {
        console.error('資格コードの取得に失敗しました:', e);
      }
    };

    fetchCertifications();
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    // Hub.listen の signedOut イベントで状態がリセットされる
  };

  // ヘッダー側のトグル用にカラースキームを切り替える
  const handleToggleColorScheme = () => {
    setColorScheme(colorScheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <AppShell
      padding="md"
      header={{ height: 64 }}
    >
      <AppShell.Header>
        {/* ヘッダーにタイトルとナビゲーションアイコンを並べる */}
        <Group h="100%" px="md" justify="space-between">
          <UnstyledButton onClick={() => navigate('/Study')} style={{ cursor: 'pointer' }} aria-label="ホームへ移動" role="link">
            <Text fw={500} ff='"Lora", Georgia, serif' fz="xl" lh={1}>CloudHub</Text>
          </UnstyledButton>
          <Group gap="xs">
            <Tooltip label="学習画面へ" withArrow>
              <ActionIcon
                variant="subtle"
                aria-label="Studyへ移動"
                onClick={() => navigate('/Study')}
              >
                <IconSchool size={20} />
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" withinPortal>
              <Tooltip label="アカウント" withArrow>
                <Menu.Target>
                  <ActionIcon variant="subtle" aria-label="アカウント">
                    <IconUser size={20} />
                  </ActionIcon>
                </Menu.Target>
              </Tooltip>
              <Menu.Dropdown>
                <Menu.Label>{user?.signInDetails?.loginId ?? user?.username ?? ''}</Menu.Label>
                <Menu.Item
                  leftSection={<IconAward size={14} />}
                  onClick={() => navigate('/Certifications')}
                >
                  Certifications
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            {/* admin ユーザーのみ管理画面アイコンを表示する */}
            {adminUser && (
              <Tooltip label="管理画面" withArrow>
                <ActionIcon
                  variant="subtle"
                  aria-label="管理画面"
                  onClick={() => navigate('/Admin')}
                >
                  <IconSettings size={20} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label={colorScheme === 'dark' ? 'ライトモードに切り替え' : 'ダークモードに切り替え'} withArrow>
              <ActionIcon
                variant="subtle"
                aria-label={colorScheme === 'dark' ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
                onClick={handleToggleColorScheme}
              >
                {colorScheme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        {/* メインを中央寄せし、コンテンツ幅を最大1200pxに制限して見栄えを整える */}
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box style={{ maxWidth: 1600, width: '100%' }}>
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/Study" replace />}
              />
              <Route
                path='/Study'
                element={
                  !user ? (
                    <>
                      <Modal
                        opened={isModalOpen}
                        onClose={() => {}}
                        closeOnClickOutside={false}
                        closeOnEscape={false}
                        withCloseButton={false}
                      >
                        <Login onLogin={() => {}} />
                      </Modal>
                    </>
                  ) : (
                    <Study onLogout={handleLogout} user={user} />
                  )
                }
              />
              <Route
                path="/Certifications"
                element={
                  !user ? (
                    <Navigate to="/Study" replace />
                  ) : (
                    <Certifications />
                  )
                }
              />
              <Route
                path="/Admin/*"
                element={
                  // AdminRoute が admin 権限を確認し、権限なしは /Study にリダイレクトする
                  <AdminRoute user={user}>
                    <Admin />
                  </AdminRoute>
                }
              />
              {certificationCodes.map((code) => (
                <Route
                  key={code}
                  path={`/Study/${code}`}
                  element={
                    // 取得した資格コードごとに ExamViewWrapper を生成する
                    <ExamViewWrapper
                      selectedCertification={code}
                      setSelectedCertification={code}
                    />
                  }
                />
              ))}
            </Routes>
          </Box>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}

export default App;
